import { gsap, ScrollTrigger } from "@gsap";

const PAGE_FRAME = 8;
const PEEK = 150; // Onder de intro: hoeveel van de volgende kaart zichtbaar blijft onder de actieve kaart (Figma mobiel).
const media = gsap.matchMedia();

const adminBar = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--wp-admin--admin-bar--height")) || 0;

document.querySelectorAll(".culture_scroller-layout").forEach((layout) => {
	const stage = layout.querySelector(".culture-stage");
	const cards = [...layout.querySelectorAll(".culture-card")];
	const cardsEl = layout.querySelector(".culture-cards");
	const backgrounds = new Map([...layout.querySelectorAll(".background-card")].map((el) => [parseInt(el.dataset.backgroundIndex, 10), el]));
	const defaultBackground = layout.querySelector(".background-layer:not(.background-card)");
	const pagination = layout.querySelector(".culture-pagination");
	if (!stage || cards.length < 2) return;

	// Op desktop staan de kaarten rechts en lopen ze in het midden van de kolom; daaronder staan ze onder de intro, bovenaan hun vak.
	media.add("(prefers-reduced-motion: no-preference)", () => {
		const stacked = window.matchMedia("(max-width: 991px)").matches;
		const frame = window.matchMedia("(min-width: 768px)").matches ? PAGE_FRAME : 0;
		const anchor = stacked ? 0 : 0.5;

		let current = -1;
		let progressValue = 0;

		layout.classList.add("has-scroll-motion");

		// Bolletjes: de component geeft de lege container, de bolletjes komen hier.
		let dots = [];
		if (pagination && !stacked) {
			pagination.innerHTML = "";
			dots = cards.map((card, i) => {
				const dot = document.createElement("button");
				dot.type = "button";
				dot.className = "swiper-pagination-bullet";
				dot.setAttribute("aria-label", card.querySelector(".culture-card-title")?.textContent.trim() || `Kaart ${i + 1}`);
				pagination.appendChild(dot);
				return dot;
			});
		}

		// Onder de desktop is de stage zo hoog als intro + hoogste kaart + de rand van de volgende; anders blijft er onder de laatste kaart een leeg scherm over.
		// Via een CSS-variabele: ScrollTrigger zet zelf een inline hoogte op de gepinde stage en die wint van een inline hoogte uit deze functie.
		const syncHeight = () => {
			if (!stacked) return;
			const grid = layout.querySelector(".culture-grid");
			const intro = layout.querySelector(".intro-col");
			const styles = getComputedStyle(grid);
			const tallest = Math.max(...cards.map((card) => card.offsetHeight));
			const cardGap = parseFloat(getComputedStyle(cardsEl).rowGap) || 0;
			const cardsTop = parseFloat(getComputedStyle(cardsEl).top) || 0;
			const wanted = parseFloat(styles.paddingTop) + intro.offsetHeight + (parseFloat(styles.rowGap) || 0) + cardsTop + tallest + cardGap + PEEK;
			layout.style.setProperty("--culture-stage-height", `${Math.round(Math.min(wanted, window.innerHeight - frame * 2 - adminBar()))}px`);
		};

		const setActive = (index) => {
			if (index === current) return;
			current = index;
			cards.forEach((card, i) => {
				card.classList.toggle("is-active", i === index);
				card.setAttribute("aria-hidden", i === index ? "false" : "true");
			});
			dots.forEach((dot, i) => {
				dot.classList.toggle("swiper-pagination-bullet-active", i === index);
				if (i === index) dot.setAttribute("aria-current", "step");
				else dot.removeAttribute("aria-current");
			});
		};

		const render = (value) => {
			progressValue = Math.max(0, Math.min(cards.length - 1, value));
			const gap = parseFloat(getComputedStyle(cardsEl).rowGap) || 80;
			const tallest = Math.max(...cards.map((card) => card.offsetHeight));
			const step = tallest + gap;
			const space = cardsEl.clientHeight;

			cards.forEach((card, i) => {
				const delta = i - progressValue;
				const distance = Math.min(Math.abs(delta), 1);

				gsap.set(card, {
					y: anchor * (space - card.offsetHeight) + delta * step,
					autoAlpha: Math.abs(delta) <= 1.35 ? 1 : 0,
					scale: 1 - 0.04 * distance,
					zIndex: cards.length - Math.round(Math.abs(delta) * 10),
					pointerEvents: Math.abs(delta) < 0.45 ? "auto" : "none",
					"--d": distance,
				});
			});

			// De achtergrond gaat mee: de eigen afbeelding van een kaart vervangt de standaard, met een crossfade tussen twee kaarten.
			const lower = Math.floor(progressValue);
			const upper = Math.ceil(progressValue);
			const blend = progressValue - lower;
			const alphas = new Map([[lower, lower === upper ? 1 : 1 - blend], [upper, lower === upper ? 1 : blend]]);
			let covered = 0;
			backgrounds.forEach((el, index) => {
				const alpha = alphas.get(index) || 0;
				covered = Math.max(covered, alpha);
				gsap.set(el, { autoAlpha: alpha });
			});
			if (defaultBackground) gsap.set(defaultBackground, { autoAlpha: 1 - covered });

			setActive(Math.round(progressValue));
		};

		// Vóór de trigger: ScrollTrigger meet de stage bij het vastpinnen.
		syncHeight();

		const trigger = ScrollTrigger.create({
			trigger: stage,
			start: () => `top top+=${frame + adminBar()}`,
			end: () => `+=${(cards.length - 1) * Math.max(window.innerHeight * 0.72, 560)}`,
			pin: true,
			pinSpacing: true,
			scrub: 0.65,
			snap: { snapTo: 1 / (cards.length - 1), duration: { min: 0.25, max: 0.6 }, delay: 0.12, ease: "power2.out", inertia: false, directional: false },
			anticipatePin: 1,
			invalidateOnRefresh: true,
			onRefreshInit: syncHeight,
			onRefresh: () => render(progressValue),
			onUpdate: (self) => render(self.progress * (cards.length - 1)),
		});

		// Lettertypen en afbeeldingen veranderen de hoogtes; zonder verversing klopt de pinafstand niet meer en verspringt alles eronder (de footer).
		const refresh = () => ScrollTrigger.refresh();
		document.fonts?.ready.then(refresh);
		window.addEventListener("load", refresh, { once: true });

		render(0);

		const goTo = (index) => {
			const target = trigger.start + (trigger.end - trigger.start) * (index / (cards.length - 1));
			window.scrollTo({ top: Math.round(target), behavior: "smooth" });
		};
		const handlers = dots.map((dot, i) => {
			const handler = () => goTo(i);
			dot.addEventListener("click", handler);
			return handler;
		});

		return () => {
			trigger.kill();
			layout.style.removeProperty("--culture-stage-height");
			dots.forEach((dot, i) => dot.removeEventListener("click", handlers[i]));
			if (pagination) pagination.innerHTML = "";
			layout.classList.remove("has-scroll-motion");
			cards.forEach((card) => {
				card.classList.remove("is-active");
				card.removeAttribute("aria-hidden");
				gsap.set(card, { clearProps: "all" });
			});
			[...backgrounds.values(), defaultBackground].filter(Boolean).forEach((el) => gsap.set(el, { clearProps: "opacity,visibility" }));
		};
	});
});
